import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, LineChart, Loader2 } from 'lucide-react';
import { TradeStrategyCards } from '@/components/TradeStrategyCards';
import { MarketTradeIdeasPanel } from '@/components/MarketTradeIdeasPanel';
import {
  tradeStrategiesService,
  type MarketTradeIdeasPayload,
  type TradeStrategyDetail,
  type TradeStrategySummary,
} from '@/services/api';

function directionBadgeClass(direction: string | undefined): string {
  switch (direction?.toLowerCase()) {
    case 'bullish':
    case 'long':
      return 'bg-emerald-50 text-emerald-800';
    case 'bearish':
    case 'short':
      return 'bg-rose-50 text-rose-800';
    case 'mixed':
      return 'bg-violet-50 text-violet-800';
    default:
      return 'bg-amber-50 text-amber-800';
  }
}

function toTradeIdeasPayload(strategy: TradeStrategyDetail): MarketTradeIdeasPayload {
  return {
    metadata: {
      reference_date: strategy.reference_date,
      generator: strategy.generator ?? undefined,
      trade_count: strategy.trade_count,
      analog_count: undefined,
    },
    situation_headline: strategy.situation_headline ?? strategy.summary,
    trades: strategy.trades ?? [],
    disclaimer: strategy.disclaimer ?? undefined,
  };
}

function StrategyDetail({ strategy }: { strategy: TradeStrategyDetail }) {
  const tradeIdeas = toTradeIdeasPayload(strategy);

  return (
    <article className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-600 text-sm font-medium text-white">
          {strategy.rank}
        </span>
        <span
          className={`rounded-lg px-2.5 py-0.5 text-xs capitalize ${directionBadgeClass(strategy.direction)}`}
        >
          {strategy.direction}
        </span>
        {strategy.horizon ? (
          <span className="rounded-lg bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
            {String(strategy.horizon).replace(/_/g, ' ')}
          </span>
        ) : null}
        {strategy.entry_date ? (
          <span className="rounded-lg bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
            Entry {strategy.entry_date}
          </span>
        ) : null}
        {strategy.avoid_count != null && strategy.avoid_count > 0 ? (
          <span className="rounded-lg bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800">
            {strategy.avoid_count} avoid
          </span>
        ) : null}
        {strategy.ytd_performance?.ytd_return_pct != null ? (
          <span
            className={`rounded-lg px-2.5 py-0.5 text-xs font-medium tabular-nums ${
              strategy.ytd_performance.ytd_return_pct > 0
                ? 'bg-emerald-50 text-emerald-800'
                : strategy.ytd_performance.ytd_return_pct < 0
                  ? 'bg-rose-50 text-rose-800'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {strategy.ytd_performance.ytd_label ?? 'YTD'}{' '}
            {strategy.ytd_performance.ytd_return_pct > 0 ? '+' : ''}
            {strategy.ytd_performance.ytd_return_pct.toFixed(1)}%
            {strategy.ytd_performance.ytd_benchmark_pct != null
              ? ` · SPY ${strategy.ytd_performance.ytd_benchmark_pct > 0 ? '+' : ''}${strategy.ytd_performance.ytd_benchmark_pct.toFixed(1)}%`
              : ''}
          </span>
        ) : null}
      </div>

      <h1 className="mb-3 text-3xl tracking-tight text-gray-900">{strategy.strategy_name}</h1>

      {strategy.strategy_description ? (
        <p className="mb-4 text-base leading-relaxed text-gray-600">{strategy.strategy_description}</p>
      ) : null}

      {strategy.summary ? (
        <p className="mb-8 text-sm leading-relaxed text-gray-500">{strategy.summary}</p>
      ) : null}

      {strategy.tags?.length ? (
        <div className="mb-8 flex flex-wrap gap-2">
          {strategy.tags.map((tag) => (
            <span key={tag} className="rounded-lg bg-gray-100 px-3 py-1 text-xs text-gray-600">
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
        <MarketTradeIdeasPanel tradeIdeas={tradeIdeas} embedded />
      </div>
    </article>
  );
}

export function TradesPage() {
  const navigate = useNavigate();
  const { strategyId, referenceDate } = useParams<{
    strategyId?: string;
    referenceDate?: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<{
    reference_date: string;
    as_of_label: string;
    strategies: TradeStrategySummary[];
    conviction_filter?: string | null;
  } | null>(null);
  const [detail, setDetail] = useState<TradeStrategyDetail | null>(null);

  const isDetailView = Boolean(strategyId);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      if (strategyId) {
        const list = await tradeStrategiesService.getLatest(referenceDate);
        const date = referenceDate ?? list?.reference_date;
        if (!date) {
          if (!cancelled) {
            setError('No trade strategies are available yet.');
            setDetail(null);
            setLoading(false);
          }
          return;
        }

        const response = await tradeStrategiesService.getStrategy(date, strategyId);
        if (cancelled) return;
        if (!response?.strategy) {
          setError('Trade strategy not found.');
          setDetail(null);
        } else {
          setDetail(response.strategy);
          setPayload(
            list ?? {
              reference_date: response.reference_date,
              as_of_label: response.reference_date,
              strategies: [],
            },
          );
        }
      } else {
        const response = await tradeStrategiesService.getLatest(referenceDate);
        if (cancelled) return;
        if (!response?.strategies?.length) {
          setError('No trade strategies are available yet.');
          setPayload(null);
        } else {
          setPayload(response);
        }
      }

      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [strategyId, referenceDate]);

  const pageSubtitle = useMemo(() => {
    if (detail) {
      const conviction =
        detail.conviction_filter === 'high' ? ' · High conviction' : '';
      return `${detail.reference_date}${conviction}`;
    }
    const conviction =
      payload?.conviction_filter === 'high' ? ' · High conviction' : '';
    return `${payload?.as_of_label ?? 'Trading strategies'}${conviction}`;
  }, [detail, payload]);

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="border-b border-gray-200 bg-white"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() =>
              isDetailView
                ? navigate('/market/trades')
                : navigate('/dashboard')
            }
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label={isDetailView ? 'Back to trades' : 'Back to dashboard'}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600">
              <LineChart className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="tracking-tight text-gray-900">Trades</h2>
              <p className="truncate text-sm text-gray-500">{pageSubtitle}</p>
            </div>
          </div>
        </div>
      </motion.header>

      <main className="mx-auto w-full max-w-7xl px-6 py-8 md:py-10">
        {loading ? (
          <div className="flex items-center justify-center gap-3 rounded-3xl border border-gray-200 bg-white px-6 py-24">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="text-gray-500">Loading trade strategies…</span>
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-gray-200 bg-white px-6 py-24 text-center">
            <p className="text-gray-600">{error}</p>
          </div>
        ) : isDetailView && detail ? (
          <StrategyDetail strategy={detail} />
        ) : payload ? (
          <div>
            <div className="mb-8">
              <h1 className="mb-2 text-3xl tracking-tight text-gray-900">Trading strategies</h1>
              <p className="text-sm text-gray-500">
                {payload.strategies.length} strategies for {payload.as_of_label}
                {payload.conviction_filter === 'high' ? ' · High conviction only' : ''}
              </p>
            </div>
            <TradeStrategyCards
              strategies={payload.strategies}
              onStrategyClick={(strategy) =>
                navigate(`/market/trades/${payload.reference_date}/${strategy.id}`)
              }
            />
          </div>
        ) : null}
      </main>
    </div>
  );
}
